import { useCallback } from 'react';

import qs from 'qs';
import { useSelector } from 'react-redux';

import {
  getCurrentPullRequest,
  getCurrentPullRequestInTopicDiffMode,
  getPullRequestCompareSpecDiffStatUrl,
  getPullRequestCompareSpecDiffUrl,
} from 'src/redux/pull-request/selectors';
import {
  qsDefaultOptions,
  normalizeDiffParams,
  normalizeDiffStatParams,
  addQueryParams,
} from 'src/redux/pull-request/urls/url-utils';
import { getIsLazyDiffstatEnabled } from 'src/selectors/feature-selectors';

const baseUrl = '/!api/2.0/repositories';
const internalUrl = '/!api/internal/repositories';

type DiffUrlBuilders = {
  diffUrlBuilder: (
    ignoreWhitespace?: boolean,
    includeFilesOverLimit?: boolean
  ) => string;
  diffStatUrlBuilder: (
    ignoreWhitespace?: boolean,
    includeFilesOverLimit?: boolean,
    useForkMergebase?: boolean
  ) => string;
};

type DiffWithConflictsUrlBuilders = DiffUrlBuilders & {
  conflictsUrl: () => string;
};

/**
 * A hook to provide the urls needed to fetch a diff for a pull request
 * @param iterativeReviewMode whether the diff is being viewed with iterative review on
 * @param id  the id of the pull request
 * @param sourceRepositoryFullSlug the workspace/repo slug of the source repo
 * @param sourceHash what source hash to diff against
 * @param destHash  what dest hash to diff against
 * @param destinationFullSlug  the workspace/repo slug of the destination repo
 * @returns a DiffUrlBuilders object with the diffUrlBuilder and diffStatUrlBuilder functions
 */
export const usePullRequestDiffUrls = (
  iterativeReviewMode: boolean,
  id: string | number,
  sourceRepositoryFullSlug: string,
  sourceHash: string,
  destHash: string | undefined,
  destinationFullSlug: string
): DiffUrlBuilders => {
  /*
    By this time, the PR resource will have been fetched and the diff/diffstat links
    should already be populated in redux so we should be able to use the repository
    diff endpoints, and save a redirect. We use the PR diff endpoints as a fallback,
    but don't expect it to be called.
  */
  let diffUrl = useSelector(getPullRequestCompareSpecDiffUrl) || '';
  let diffStatUrl = useSelector(getPullRequestCompareSpecDiffStatUrl) || '';
  const currentPullRequest = useSelector(getCurrentPullRequest);
  let excludeFiles = currentPullRequest?.latest_excluded_files_version;
  let topicDiff: boolean | null = useSelector(
    getCurrentPullRequestInTopicDiffMode
  );
  if (!diffUrl || !diffStatUrl) {
    diffUrl = `${baseUrl}/${sourceRepositoryFullSlug}/pullrequests/${id}/diff`;
    diffStatUrl = `${baseUrl}/${sourceRepositoryFullSlug}/pullrequests/${id}/diffstat`;
    // backend redirect will override topic diff param
    topicDiff = null;
  }

  try {
    // If the selector above returns null and the fallback diffUrl value is used, it is unable to convert
    // to a URL, so we need to catch it and move on.
    // fallback option is to get the exclude_files from the URL if for some reason it's not in the PR data
    if (typeof excludeFiles !== 'string' || excludeFiles === '') {
      excludeFiles = new URL(diffUrl).searchParams.get('exclude_files');
    }
  } catch {
    // Do nothing
  }

  const useLazyDiffstat = useSelector(getIsLazyDiffstatEnabled);

  // Destination hash might be blank if the commits haven't loaded and the iterative review selector hasn't been set yet
  // Only do this if iterative review is enabled
  if (sourceHash && iterativeReviewMode) {
    // Use the generic diff endpoints instead of the hydrated data because we can't guarantee we need to fetch the
    // whole diff from the server, so we reconstruct the URLs manually.
    if (!destHash) {
      // The destination hash in this case is the single commit hash. We construct the URL this way to only fetch the diff
      // for a singular commit.
      diffUrl = `${baseUrl}/${destinationFullSlug}/diff/${sourceHash}`;
      diffStatUrl = `${
        useLazyDiffstat ? internalUrl : baseUrl
      }/${destinationFullSlug}/diffstat/${sourceHash}`;
    } else if (sourceRepositoryFullSlug) {
      // We need to put the source and destination repo slugs in the URL because we're looking at a fork.
      diffUrl = `${baseUrl}/${destinationFullSlug}/diff/${sourceRepositoryFullSlug}:${sourceHash}%0D${destinationFullSlug}:${destHash}?from_pullrequest_id=${id}`;
      diffStatUrl = `${
        useLazyDiffstat ? internalUrl : baseUrl
      }/${destinationFullSlug}/diffstat/${sourceRepositoryFullSlug}:${sourceHash}%0D${destinationFullSlug}:${destHash}?from_pullrequest_id=${id}`;
    } else {
      // We are looking at the CSLR view or a specific range of commits
      // which requires putting the current repository in the URL instead of the source repository for forks
      diffUrl = `${baseUrl}/${destinationFullSlug}/diff/${destinationFullSlug}:${sourceHash}%0D${destHash}`;
      diffStatUrl = `${
        useLazyDiffstat ? internalUrl : baseUrl
      }/${destinationFullSlug}/diffstat/${destinationFullSlug}:${sourceHash}%0D${destHash}`;
    }
  }

  const diffUrlBuilder = useCallback(
    (
      ignoreWhitespace?: boolean,
      includeFilesOverLimit?: boolean,
      useForkMergebase?: boolean
    ) => {
      const url = addQueryParams(diffUrl, {
        ...normalizeDiffParams({
          ignoreWhitespace,
          includeFilesOverLimit,
          useForkMergebase,
          topicDiff,
          excludeFiles,
        }),
        // Instead of checking the partial-diffs feature on the client, we can
        // always pass the query parameter and let the server check the flag
        allow_partial_diffs: 1,
      });

      return url;
    },
    /* by including owner, slug, and id as dependencies but not diffUrl,
    we are allowing the callback function to be redefined when navigating
    to another pull request, but not if diffUrl/diffStatUrl values have been
    hydrated after the initial fetch */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, sourceRepositoryFullSlug, sourceHash, destHash]
  );

  const diffStatUrlBuilder = useCallback(
    (ignoreWhitespace?: boolean, useForkMergebase?: boolean) => {
      const url = addQueryParams(
        diffStatUrl,
        normalizeDiffStatParams({
          ignoreWhitespace,
          useForkMergebase,
          topicDiff,
          excludeFiles,
        })
      );
      return url;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, sourceRepositoryFullSlug, sourceHash, destHash]
  );

  return { diffUrlBuilder, diffStatUrlBuilder };
};

export const useDiffSetUrls = (
  sourceRepoFullSlug: string,
  sourceHash: string,
  destRepoFullSlug?: string,
  destHash?: string
): DiffWithConflictsUrlBuilders => {
  const useLazyDiffstat = useSelector(getIsLazyDiffstatEnabled);

  const diffUrl = destHash
    ? `${baseUrl}/${sourceRepoFullSlug}/diff/${sourceRepoFullSlug}:${sourceHash}%0D${destRepoFullSlug}:${destHash}`
    : `${baseUrl}/${sourceRepoFullSlug}/diff/${sourceHash}`;

  const diffStatUrl = destHash
    ? `${
        useLazyDiffstat ? internalUrl : baseUrl
      }/${sourceRepoFullSlug}/diffstat/${sourceRepoFullSlug}:${sourceHash}%0D${destRepoFullSlug}:${destHash}`
    : `${
        useLazyDiffstat ? internalUrl : baseUrl
      }/${sourceRepoFullSlug}/diffstat/${sourceHash}`;

  const diffUrlBuilder = useCallback(
    (
      ignoreWhitespace?: boolean,
      includeFilesOverLimit?: boolean,
      useForkMergebase?: boolean
    ) => {
      const query = destHash
        ? qs.stringify(
            normalizeDiffParams({
              topicDiff: true,
              ignoreWhitespace,
              includeFilesOverLimit,
              useForkMergebase,
            }),
            qsDefaultOptions
          )
        : qs.stringify(
            normalizeDiffParams({
              ignoreWhitespace,
              includeFilesOverLimit,
              useForkMergebase,
            }),
            qsDefaultOptions
          );

      return `${diffUrl}${query}`;
    },
    [diffUrl, destHash]
  );

  const diffStatUrlBuilder = useCallback(
    (ignoreWhitespace?: boolean, useForkMergebase?: boolean) => {
      const query = destHash
        ? qs.stringify(
            normalizeDiffStatParams({
              topicDiff: true,
              ignoreWhitespace,
              useForkMergebase,
            }),
            qsDefaultOptions
          )
        : qs.stringify(
            normalizeDiffStatParams({
              ignoreWhitespace,
              useForkMergebase,
            }),
            qsDefaultOptions
          );
      return `${diffStatUrl}${query}`;
    },
    [diffStatUrl, destHash]
  );

  const conflictsUrl = () => {
    const url = destHash
      ? `${internalUrl}/${sourceRepoFullSlug}/file-conflicts/${sourceHash}%0D${destRepoFullSlug}:${destHash}`
      : `${internalUrl}/${sourceRepoFullSlug}/file-conflicts/${sourceRepoFullSlug}:${sourceHash}`;
    return url;
  };

  return { diffUrlBuilder, diffStatUrlBuilder, conflictsUrl };
};
